import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Loading } from "@/components/ui";
import { UsageMetricCard } from "@/components/billing/UsageMetricCard";
import { CreditPacksList } from "@/components/billing/CreditPacksList";
import { BuyCreditsModal } from "@/components/billing/BuyCreditsModal";
import { BillingAPI, type Metric, type UsageResponse } from "@/services/billing";

import { SettingsLayout } from "../settings-layout";
import { SubscriptionPlanPanel } from "./SubscriptionPlanPanel";

type Status = "idle" | "loading";

const METRIC_VALUES: readonly Metric[] = ["ai_conversations", "ai_messages"];

function isMetric(value: string | null): value is Metric {
  return value !== null && (METRIC_VALUES as readonly string[]).includes(value);
}

export function SettingsUsage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [buyMetric, setBuyMetric] = useState<Metric | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    BillingAPI.getUsage()
      .then((data) => {
        if (cancelled) return;
        setUsage(data);
        setStatus("idle");
      })
      .catch(() => {
        if (cancelled) return;
        setError("Não foi possível carregar o uso atual. Tente novamente.");
        setStatus("idle");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const buyParam = searchParams.get("buy");
    if (isMetric(buyParam)) {
      setBuyMetric(buyParam);
    }
  }, [searchParams]);

  function handleBuyMore(metric: Metric) {
    setBuyMetric(metric);
  }

  function handleModalOpenChange(open: boolean) {
    if (!open) {
      setBuyMetric(null);
      if (searchParams.has("buy")) {
        const next = new URLSearchParams(searchParams);
        next.delete("buy");
        setSearchParams(next, { replace: true });
      }
    }
  }

  const periodEndForMetric: string | null =
    buyMetric && usage ? usage[buyMetric].period_end : null;

  return (
    <SettingsLayout
      current="usage"
      title="Plano e consumo"
      lede="O plano da clínica, o consumo do período e os créditos extras."
    >
      <SubscriptionPlanPanel />

      <div className="mt-8">
        {status === "loading" && (
          <div className="flex items-center justify-center py-24">
            <Loading />
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-lg border border-app-danger/30 bg-app-danger/10 p-4 text-sm text-app-fg-strong">
            {error}
          </div>
        )}

        {usage && (
          <>
            <div className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-2">
              <UsageMetricCard
                metric="ai_conversations"
                allowance={usage.ai_conversations}
                onBuyMore={handleBuyMore}
              />
              <UsageMetricCard
                metric="ai_messages"
                allowance={usage.ai_messages}
                onBuyMore={handleBuyMore}
              />
            </div>

            <section className="mb-2">
              <header className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-app-fg-strong">
                  Créditos extras
                </h2>
              </header>
              <CreditPacksList packs={usage.credit_packs} />
            </section>
          </>
        )}

        <BuyCreditsModal
          open={buyMetric !== null}
          metric={buyMetric}
          periodEnd={periodEndForMetric}
          onOpenChange={handleModalOpenChange}
        />
      </div>
    </SettingsLayout>
  );
}
